/**
 * Builds the machine `onEnter`/`onExit` effects that project remix state into
 * other stores (editor loading, chat panel).
 *
 * This is the concrete realisation of option (A) — reactive projection. The
 * machine transitions; entering/leaving a phase runs an effect that calls a
 * narrow injected port. No imperative "also update the editor" calls are
 * sprinkled through the flow code.
 *
 * Example mapping:
 *   - enter `thinking`   → editor shows loading for the target node
 *   - exit  `thinking`   → editor clears loading (covers ready/error/abort)
 *   - enter `error`      → open the chat panel so the user sees the failure
 */

import type { RemixEffectHooks } from '../remixMachine';

import type { RemixEffectPorts } from './types';

/**
 * Create the effect hooks for a remix machine from the injected ports.
 *
 * @param ports  The cross-store ports available on this surface (any subset).
 * @returns      Effect hooks to pass to `createRemixCreateMachine` /
 *               `createRemixEditMachine`.
 */
export const buildRemixEffects = (ports: RemixEffectPorts): RemixEffectHooks => {
	const { editorLoading, chatPanel } = ports;

	return {
		// Editor loading state is tied to the `thinking` phase: show on enter,
		// clear on exit (whichever phase we move to). Targeting uses the
		// context's targetNodeId (undefined for creates without an anchor).
		onEnterThinking: editorLoading
			? (ctx) => editorLoading.setLoading(ctx.targetNodeId)
			: undefined,
		onExitThinking: editorLoading
			? (prev) => editorLoading.clearLoading(prev.targetNodeId)
			: undefined,

		// Open chat when the flow errors so the user can see the failure / retry.
		onEnterError: chatPanel ? (ctx) => chatPanel.open(ctx.conversationId) : undefined,
	};
};
